import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RepairService } from '../types/BookingTypes';
import { getPopularServices } from '../services/popularServiceService';

type Tab = 'screen' | 'battery';

const isScreen = (name: string) => /display|screen/i.test(name);
const isBattery = (name: string) => /battery/i.test(name);
const discountPct = (actual: number, marked: number) =>
  marked > actual ? Math.round(((marked - actual) / marked) * 100) : 0;

const CARD_WIDTH = 176 + 12; // w-44 (176px) + gap-3 (12px)

const PopularServices: React.FC = () => {
  const [services, setServices] = useState<RepairService[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('screen');
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    getPopularServices()
      .then(setServices)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Reset carousel when tab changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [activeTab]);

  const screenServices = services.filter((s) => isScreen(s.serviceName));
  const batteryServices = services.filter((s) => isBattery(s.serviceName));

  const tabs: { key: Tab; label: string }[] = [
    { key: 'screen', label: 'Screen Repair' },
    { key: 'battery', label: 'Battery' },
  ];

  const visibleCards = activeTab === 'screen' ? screenServices : batteryServices;
  const maxIndex = Math.max(0, visibleCards.length - 2);

  const prev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const next = () => setCurrentIndex((i) => Math.min(maxIndex, i + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      diff > 0 ? next() : prev();
    }
  };

  if (!loading && services.length === 0) return null;

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-gray-900">Popular Repairs</h2>
        <span className="text-xs text-gray-400 font-medium">Genuine Parts • Warranty</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
              activeTab === tab.key
                ? 'bg-minto-600 text-white border-minto-600 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Carousel */}
      {loading ? (
        <div className="flex gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex-shrink-0 w-44 h-56 bg-white rounded-2xl animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : (
        <div className="relative">
          {/* Left button */}
          <button
            onClick={prev}
            disabled={currentIndex === 0}
            className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center transition-opacity disabled:opacity-30"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Track */}
          <div
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div
              ref={trackRef}
              className="flex gap-3 transition-transform duration-300 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * CARD_WIDTH}px)` }}
            >
              {visibleCards.map((service) => {
                const off = discountPct(service.actualPrice, service.markedPrice);
                return (
                  <div
                    key={service.id}
                    onClick={() => navigate(`/services/${service.modelId}`)}
                    className="flex-shrink-0 w-44 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden cursor-pointer active:scale-95 transition-transform"
                  >
                    {/* Top */}
                    <div className="relative bg-gradient-to-br from-minto-50 to-pink-50 px-4 pt-4 pb-3 flex flex-col items-center">
                      {off > 0 && (
                        <span className="absolute top-2 right-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                          {off}% OFF
                        </span>
                      )}
                      {service.serviceImage ? (
                        <img
                          src={service.serviceImage}
                          alt={service.serviceName}
                          style={{ width: '6rem', height: '6rem' }}
                          className="object-contain mb-1"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-5xl mb-1">
                          {activeTab === 'screen' ? '📱' : '🔋'}
                        </span>
                      )}
                      <span className="text-xs font-semibold text-gray-700 text-center leading-tight">
                        {service.modelName}
                      </span>
                    </div>

                    {/* Bottom */}
                    <div className="px-3 pt-2 pb-3">
                      <p className="text-xs text-gray-500 mb-1 truncate">{service.serviceName}</p>
                      <div className="flex items-baseline gap-1.5 mb-2">
                        <span className="text-base font-bold text-gray-900">
                          ₹{(service.finalPrice || service.actualPrice).toLocaleString('en-IN')}
                        </span>
                      </div>
                      <button className="w-full bg-minto-600 hover:bg-minto-700 active:bg-minto-800 text-white text-xs font-semibold py-1.5 rounded-lg transition-colors">
                        Book Now
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right button */}
          <button
            onClick={next}
            disabled={currentIndex >= maxIndex}
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center transition-opacity disabled:opacity-30"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Dot indicators */}
          {visibleCards.length > 2 && (
            <div className="flex justify-center gap-1.5 mt-3">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`rounded-full transition-all duration-200 ${
                    i === currentIndex
                      ? 'w-4 h-1.5 bg-minto-600'
                      : 'w-1.5 h-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PopularServices;
